import { createPortal } from 'react-dom'
import { useEffect, useLayoutEffect, useRef, useState, type ReactElement } from 'react'
import type { PermissionValue } from '@/types/knowledge_graph'

export function CrudLetterBadge ({ initial, value, uncertain, followupQuestion }: {
  initial: string
  value: PermissionValue | undefined
  uncertain: boolean
  followupQuestion?: string | null
}): ReactElement {
  const [hovered, setHovered] = useState(false)
  const [pinned, setPinned] = useState(false)
  const anchorRef = useRef<HTMLSpanElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const [placement, setPlacement] = useState<'below' | 'above'>('below')
  const hasFollowupQuestion = followupQuestion != null && followupQuestion !== ''
  const hasQuestion = uncertain && hasFollowupQuestion
  const open = hasQuestion && (hovered || pinned)

  useEffect(() => {
    if (open) setRect(anchorRef.current?.getBoundingClientRect() ?? null)
  }, [open])

  useLayoutEffect(() => {
    if (!open || (rect == null)) return
    const popoverHeight = popoverRef.current?.offsetHeight ?? 0
    const spaceBelow = window.innerHeight - rect.bottom
    setPlacement(spaceBelow < popoverHeight + 12 ? 'above' : 'below')
  }, [open, rect])

  useEffect(() => {
    if (!pinned) return
    function handleClickOutside (e: MouseEvent): void {
      if (anchorRef.current?.contains(e.target as Node) !== true) setPinned(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [pinned])

  const shown = value === 'allowed' || uncertain
  const color = uncertain ? 'var(--bp-warning)' : value === 'allowed' ? 'var(--bp-ok)' : 'var(--bp-ink3)'

  return (
    <span
      ref={anchorRef}
      style={{
        display: 'inline-block',
        width: 13,
        fontSize: 12,
        fontWeight: 700,
        textAlign: 'center',
        color,
        cursor: hasQuestion ? 'pointer' : undefined
      }}
      onMouseEnter={() => { if (hasQuestion) setHovered(true) }}
      onMouseLeave={() => setHovered(false)}
      onClick={() => { if (hasQuestion) setPinned((p) => !p) }}
    >
      {shown ? initial : '−'}
      {open && (rect != null) && createPortal(
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            ...(placement === 'below'
              ? { top: rect.bottom + 6 }
              : { top: 'auto', bottom: window.innerHeight - rect.top + 6 }
            ),
            left: Math.min(rect.left, window.innerWidth - 300),
            width: 280,
            zIndex: 1000,
            background: 'var(--bp-surface)',
            border: '1.5px solid var(--bp-border)',
            borderRadius: 8,
            padding: '10px 12px',
            boxShadow: '0 12px 32px rgba(0,0,0,.18)'
          }}
        >
          <div style={{ fontSize: 11, color: 'var(--bp-ink2)', lineHeight: 1.5 }}>
            {followupQuestion}
          </div>
        </div>,
        document.body
      )}
    </span>
  )
}
